/**
 * Email Validation Utility
 * Supports modern email formats including Gmail aliases (e.g., user+tag@gmail.com)
 */

/**
 * Validates email address against a comprehensive pattern
 * Supports:
 * - Standard emails: user@domain.com
 * - Gmail aliases: user+tag@gmail.com
 * - Subdomain emails: user@mail.domain.co.uk
 * - Various TLDs: .com, .co, .io, etc.
 * 
 * @param {string} email - Email address to validate
 * @returns {boolean} - True if email is valid
 */
export const isValidEmail = (email) => {
	// RFC 5322 simplified pattern that supports common email formats
	// Including: alphanumeric, dots, hyphens, plus signs (for aliases)
	const emailPattern = /^[a-zA-Z0-9._+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
	
	if (!emailPattern.test(email)) {
		return false;
	}

	// Additional validation checks
	const [localPart, domain] = email.split("@");

	// Local part shouldn't start or end with dot, hyphen, or plus
	if (/^[.\-+]|[.\-+]$/.test(localPart)) {
		return false;
	}

	// No consecutive special characters like "..", "--", "++"
	if (/[.\-+]{2,}/.test(localPart)) {
		return false;
	}

	// Domain shouldn't start or end with dot or hyphen
	if (/^[.\-]|[.\-]$/.test(domain)) {
		return false;
	}

	// Valid email
	return true;
};

/**
 * Get user-friendly error message for invalid email
 * @param {string} email - Email address that failed validation
 * @returns {string} - Error message
 */
export const getEmailErrorMessage = (email) => {
	if (!email || email.trim() === "") {
		return "Email address is required";
	}

	if (!email.includes("@")) {
		return "Email must contain @ symbol";
	}

	if (!isValidEmail(email)) {
		return "Please enter a valid email address (e.g., user@example.com or user+tag@gmail.com)";
	}

	return "Invalid email address";
};

/**
 * Validate multiple email addresses
 * @param {string[]} emails - Array of email addresses
 * @returns {object} - { valid: string[], invalid: string[] }
 */
export const validateMultipleEmails = (emails) => {
	return emails.reduce(
		(acc, email) => {
			if (isValidEmail(email.trim())) {
				acc.valid.push(email.trim());
			} else {
				acc.invalid.push(email.trim());
			}
			return acc;
		},
		{ valid: [], invalid: [] }
	);
};
